
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AppState, ShowRequest } from '../../core';
import { CorrelationRecord } from '../../core/models';
import { DefaultCellRendererComponent, numberToFilterString, TableViewQueryCriteria } from '../../shared/components/table-view/table-view-component-base';
import { firstValueFrom } from 'rxjs';
import { VolunteerHandsetRec } from '../../volunteer/models/volunteer-handset';
import { VolunteerEditComponent } from '../../volunteer/volunteer-edit/volunteer-edit.component';
import { UserEditComponent } from '../../user/user-edit/user-edit.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { PathTestService, PostProcessingService, UserService } from '../../core/services';
import { VolunteerService } from '../../volunteer/services/volunteer.service';
import { TestSeqService } from '../../core/services/test-seq.service';
import { TestDocArgs } from '../../path-test/path-test.component';


@Component({
    selector: '[fes-correlation-table-cell-renderer]',
    templateUrl: 'correlation-table-cell.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
  })
  export class CorrelationTableCellComponent extends DefaultCellRendererComponent<CorrelationRecord> {

    constructor(private datePipe: DatePipe,
      public appState: AppState,
      public modalService: BsModalService,
      public testbedUserService: UserService,
      public volunteerService: VolunteerService,
      public testSeqService: TestSeqService,
      public pathTestService: PathTestService,
      public postProcessingService: PostProcessingService,
    ) {
      super();
    }

    showBuilding(rec:CorrelationRecord){
      this.tableComponent.applySelectedRecords({recs:[rec]});
      this.showRequestEmitter.next( new ShowRequest('tab', 'building', rec.buildingid, null))
    }


    showPoint(rec:CorrelationRecord){
      this.tableComponent.applySelectedRecords({recs:[rec]});
      this.showRequestEmitter.next( new ShowRequest('tab', 'point', rec.pointid, null))
    }


    showCall(rec:CorrelationRecord){
      //const starttime = this.datePipe.transform(rec.callstarttime,'yyyy-MM-dd HH:mm:ss.SSS','UTC')
      const queryCriteria : TableViewQueryCriteria = {
        page: {
          firstRowFilters: [
            // TODO - this needs to make sense for legacy, FES dialer, and volunteer (RUT) calls
            //{ key: 'locsourceid', mode: '=', value: numberToFilterString(rec.locsourceid)},
            { key: 'sourceid', mode: '=', value: numberToFilterString(rec.callsourceid)},
            //{ key: 'starttime', mode: '=', value: starttime}
          ]
        }
      }
      this.tableComponent.applySelectedRecords({recs:[rec]});
      this.showRequestEmitter.emit( new ShowRequest('tab', 'call logs', JSON.stringify(queryCriteria), null))
    }


    showParticipantLocation(rec:CorrelationRecord){
      const sourceTable = rec.sourcetable
      let strContext = ''
      let viewName : string = null
      switch( sourceTable ) {
        case 'location_tmo_0':
          const ascertained = this.datePipe.transform(rec.locationtime,'yyyy-MM-dd HH:mm:ss.SSS','UTC')
          const queryCriteria : TableViewQueryCriteria = {
            page: {
              firstRowFilters: [
                { key: 'id', mode: '=', value: numberToFilterString(rec.locsourceid)},
                { key: 'ascertained', mode: '=', value: ascertained }
              ]
            }
          }
          viewName = 't-mobile location logs'
          strContext = JSON.stringify(JSON.stringify(queryCriteria))
          break;
        default:
          this.appState.notifyWarning(`App does not have a view for '${sourceTable||""}' table`,this,5)
      }
      if ( viewName ) {
        this.tableComponent.applySelectedRecords({recs:[rec]});
        this.showRequestEmitter.emit( new ShowRequest('tab', viewName, strContext, null))
      }
    }


    showLocation(rec:CorrelationRecord){
      //const locationtime = this.datePipe.transform(record.locationtime,'yyyy-MM-dd HH:mm:ss.SSS','UTC')
      const queryCriteria : TableViewQueryCriteria = {
        page: {
          firstRowFilters: [
            { key: 'callsourceid', mode: '=', value: numberToFilterString(rec.callsourceid)},
            { key: 'sourceid', mode: '=', value: numberToFilterString(rec.locsourceid)},
            { key: 'type', mode: '=', value: rec.type},
            //{ key: 'locationtime', mode: '=', value: locationtime}
          ]
        },
        //Important: Rows that are same except "type" may return in different order each query
        //Explicit sort ensures page query and actual data query return rows in same order.
        sorts: [
          { key: 'locationtime', order: 'DESC' },
          { key: 'type', order: 'ASC' },
        ]
      }
      this.tableComponent.applySelectedRecords({recs:[rec]});
      const strContext = JSON.stringify(queryCriteria);
      this.showRequestEmitter.emit( new ShowRequest('tab', 'location logs', strContext, null))
    }

    ///////////////////////////////////////////////////////////////////////////
    // Remaining methods are specific to alternate view (edit user, open testpoint, etc...)
    ///////////////////////////////////////////////////////////////////////////

    editUser = async (rec: CorrelationRecord) => {
      try {
        this.tableComponent.applySelectedRecords({recs: [rec]});
        if ( rec.calltesttype == 'rut') {
          const volunteer = await this.volunteerService.getByIdEagerly(rec.userid);
          if ( !volunteer ) {
            this.appState.notifyWarning('Volunteer not found',this,3);
            return;
          }
          const modal = VolunteerEditComponent.showModal(this.modalService,volunteer);
          let updatedHandset: VolunteerHandsetRec = null;
          const handsetSubscription = modal.content.handsetChanged.subscribe({
            next: (handsetRec) => updatedHandset = handsetRec
          })
          await firstValueFrom(modal.onHidden);
          handsetSubscription.unsubscribe();
          if ( !modal.content?.cancelled || updatedHandset) {
            this.tableComponent.load().catch(e=>this.volunteerService.publishError);
          }
        } else if ( rec.calltesttype == 'dialer') {
          const user = await this.testbedUserService.getById(rec.userid);
          if ( !user ) {
            this.appState.notifyWarning('User not found',this,3);
            return;
          }
          const initialState = {
            user
          }
          this.modalService.show(UserEditComponent, {ignoreBackdropClick: true, initialState});
        }
      } catch (e:any) {
        console.error(e);
        this.appState.notifyWarning('Could not get user details. ' + (e.message || e),this,3);
      }
    }

    showTask = async (rec: CorrelationRecord) => {
      try {
        this.tableComponent.applySelectedRecords({recs: [rec]});
        if ( rec.calltesttype == 'rut') {
          this.showRequestEmitter.emit( new ShowRequest('tab', "volunteer task", rec.taskid, null))
        } else if ( rec.calltesttype == 'dialer') {
          try {
          this.tableComponent.isLoading = true;
          // const pathTestDoc = await this.pathTestService.findOneByTestPointId(rec.testpointid);
          // if ( !pathTestDoc ) {
          //   this.appState.notifyWarning('Path test not found',this,3);
          //   return;
          // }
          // this.showRequestEmitter.emit( new ShowRequest('tab', "path test", null, pathTestDoc))
          const testDocArgs: TestDocArgs = {
            pointTestId: rec.testpointid,
            editPoint: false,
          }
          this.showRequestEmitter.emit( new ShowRequest('tab', rec.routeid ? 'drive test' : 'path test', JSON.stringify(testDocArgs), null))
          } finally {
            this.tableComponent.isLoading = false;
          }
        }
      } catch (e:any) {
        console.error(e);
        this.appState.notifyWarning('Could not get user details. ' + (e.message || e),this,3);
      }
    }

    showTestPoint = async (rec: CorrelationRecord) => {
      try {
        this.tableComponent.applySelectedRecords({recs: [rec]});
        if ( rec.calltesttype == 'rut') {
          const taskIdObj = {
            taskId: rec.taskid,
            taskPointId: parseInt(rec.testpointid)
          }
          this.showRequestEmitter.emit( new ShowRequest('tab', "volunteer task", JSON.stringify(taskIdObj), null))
        } else if ( rec.calltesttype == 'dialer') {
          const testDocArgs: TestDocArgs = {
            pointTestId: rec.testpointid,
            editPoint: true,
          }
          this.showRequestEmitter.emit( new ShowRequest('tab', rec.routeid ? 'drive test' : 'path test', JSON.stringify(testDocArgs), null))
        }
      } catch (e:any) {
        console.error(e);
        this.appState.notifyWarning('Could not get user details. ' + (e.message || e),this,3);
      }
    }

  }